/**
 * Redux thunks for authentication actions
 */

import { createAsyncThunk } from '@reduxjs/toolkit'
import AuthService from '@/services/authService'
import { ApiError } from '@/services/api'
import { User } from '@shared/types'
import { type LoginRequest, type RegisterRequest } from '@shared'

// Login thunk
export const loginUser = createAsyncThunk<
  { user: User; token: string },
  LoginRequest,
  { rejectValue: string }
>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await AuthService.login(credentials)
      return {
        user: response.user,
        token: response.token
      }
    } catch (error) {
      const apiError = error as ApiError
      return rejectWithValue(apiError.message || 'Login failed')
    }
  }
)

// Register thunk
export const registerUser = createAsyncThunk<
  { user: User; token: string },
  RegisterRequest,
  { rejectValue: string }
>(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await AuthService.register(userData)
      return {
        user: response.user,
        token: response.token
      }
    } catch (error) {
      const apiError = error as ApiError
      return rejectWithValue(apiError.message || 'Registration failed')
    }
  }
)

// Logout thunk
export const logoutUser = createAsyncThunk<
  void,
  void,
  { rejectValue: string }
>(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await AuthService.logout()
    } catch (error) {
      const apiError = error as ApiError
      // Continue with logout even if server call fails
      console.warn('Logout server call failed:', apiError.message)
    }
  }
)

// Refresh token thunk
export const refreshToken = createAsyncThunk<
  { user: User; token: string },
  void,
  { rejectValue: string }
>(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthService.refreshToken()
      return {
        user: response.user,
        token: response.token
      }
    } catch (error) {
      const apiError = error as ApiError
      return rejectWithValue(apiError.message || 'Token refresh failed')
    }
  }
)

// Validate session thunk (check if stored token is valid)
export const validateSession = createAsyncThunk<
  { user: User },
  void,
  { rejectValue: string }
>(
  'auth/validateSession',
  async (_, { rejectWithValue }) => {
    try {
      const user = await AuthService.validateSession()
      if (!user) {
        return rejectWithValue('Session invalid')
      }
      return { user }
    } catch (error) {
      const apiError = error as ApiError
      return rejectWithValue(apiError.message || 'Session validation failed')
    }
  }
)

// Get user profile thunk
export const getUserProfile = createAsyncThunk<
  { user: User },
  void,
  { rejectValue: string }
>(
  'auth/getUserProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthService.getProfile()
      return { user: response.user }
    } catch (error) {
      const apiError = error as ApiError
      return rejectWithValue(apiError.message || 'Failed to get user profile')
    }
  }
) 